import { AppConstants } from '@unpispas/upp-defs';

import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from '../base';
import { CreateObject } from '../item';
import { Place } from './place';

import { FloorAreaView, FloorAreaViewProxy } from '../views/floorarea';

import { DrawItem } from './draw';
import { QrCode } from './qrcode';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { PlaceType } from '../types';
import { DrawItemType } from '../types';

const _schema = {
    "name": "FLOORAREA",
    "fields": [
        { name: "place", type: "objid" },
        { name: "status", type: "string", default: "AC" },
        { name: "name", type: "string" },
        { name: "photo", type: "file" },
        { name: "coord_w", type: "number" },
        { name: "coord_h", type: "number" },
    ],
    relate: [
        { direction: ">", target: "PLACE", by: "place", name: "place", reason: "place", child: false, class: PlaceType },
        { direction: "<", target: "DRAWITEM", by: "area", name: "draw", reason: "draws", child: true, class: DrawItemType },
    ],
} as const;

const _FloorAreaClass = CreateObject(_schema);
if (!_FloorAreaClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class FloorArea extends _FloorAreaClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null) {
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): FloorArea {
        return this._Copy(store) as FloorArea;
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || !this.Commited){
            return;
        }

        if (relation === 'place' && related instanceof Place){
            if (related === this.place){
                related.AddDrawarea(this);
            }
        }
    }

    override get viewProxy(): FloorAreaView {
        return FloorAreaViewProxy(this, this.data);
    }

    get IsValid(): boolean {
        return !!this.status && this.status != 'DE';
    }

    get avatar(): string | null {
        const _bg = 'd3c79b';
        const _fg = '000000';
        const label = (this.name && this.name.trim()) ? this.name : '?';

        return AppConstants.baseURL + 'photo/avatar?size=128&background=' + _bg + '&color=' + _fg + '&name=' + encodeURIComponent(label);
    }

    get status(): string | null {
        return this['__base__status'];
    }

    set status(value: string) {
        if (this.status == 'DE') {
            return;
        }

        if (this.status == value) {
            return;
        }

        this['__base__status'] = value;
        if (this.place){
            if ((this.status == 'DE') && !this.Commited){
                this.place.DelDrawarea(this);
            }
            else {
                this.place.DoRefresh(this.table);
            }
        }
    }

    /** Valid QRCODE rows on this floor area (direct `QRCODE.area` or via `QRCODE.draw`). */
    get qrcodes(): QrCode[] {
        const _place = this.place;
        if (!_place){
            return [];
        }

        const _qrcodes: QrCode[] = [];
        for (const _qrcode of _place.qrcodes ?? []){
            if (!_qrcode.IsValid){
                continue;
            }

            if (_qrcode.area === this){
                _qrcodes.push(_qrcode);
                continue;
            }

            const _draw = _qrcode.draw as DrawItem | null;
            if (_draw?.IsValid && _draw.area === this){
                _qrcodes.push(_qrcode);
            }
        }

        return _qrcodes;
    }
}
